import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  ShieldAlert, Plus, Trash2, Loader2, Search, AlertTriangle,
  CheckCircle, User, FileText, X
} from 'lucide-react';
import { toast } from 'sonner';

const TAG_COLORS = {
  dairy: 'bg-blue-600', nuts: 'bg-amber-700', egg: 'bg-yellow-600',
  gluten: 'bg-orange-600', shellfish: 'bg-rose-600', soy: 'bg-green-700',
  sesame: 'bg-stone-600', fish: 'bg-cyan-700',
};

const TagBadge = ({ tag, onClick, active = true }) => (
  <Badge
    onClick={onClick}
    className={`text-white text-xs cursor-${onClick ? 'pointer' : 'default'} ${active ? (TAG_COLORS[tag] || 'bg-slate-600') : 'bg-slate-300 text-slate-600'}`}
    data-testid={`allergen-tag-${tag}`}
  >
    {tag}
  </Badge>
);

// ── Menu Item Allergen Map ──
const AllergenMapCard = ({ mapData, refresh }) => {
  const [newName, setNewName] = useState('');
  const [newTags, setNewTags] = useState([]);
  const [saving, setSaving] = useState(false);

  const tags = mapData?.allergen_tags || [];

  const toggleNewTag = (tag) => {
    setNewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const handleAdd = async () => {
    if (!newName.trim()) { toast.error('Enter an item name'); return; }
    if (newTags.length === 0) { toast.error('Select at least one allergen tag'); return; }
    setSaving(true);
    try {
      await api.addAllergenItem({ item_name: newName.trim(), allergens: newTags });
      toast.success(`Mapped "${newName.trim()}" → ${newTags.join(', ')}`);
      setNewName(''); setNewTags([]);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add item'); }
    finally { setSaving(false); }
  };

  const toggleItemTag = async (item, tag) => {
    const allergens = item.allergens.includes(tag)
      ? item.allergens.filter(t => t !== tag)
      : [...item.allergens, tag];
    try {
      await api.updateAllergenItem(item.id, { item_name: item.item_name, allergens });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove allergen mapping for "${item.item_name}"?`)) return;
    try {
      await api.deleteAllergenItem(item.id);
      toast.success(`Removed "${item.item_name}"`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <Card className="border-2 border-slate-200" data-testid="allergen-map-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldAlert className="w-5 h-5 text-[#2B4F8C]" />
          Menu Item Allergen Map
        </CardTitle>
        <p className="text-sm text-slate-500">
          Tag each lunch item with its allergens. A camper with a matching allergy is blocked from ordering it.
          Items whose NAME contains an allergen word (e.g. "Egg Salad", "Cheese", "Tuna") are auto-tagged automatically —
          use this map for items where the allergen is hidden (e.g. Pizza → dairy). Click a tag to toggle it on/off.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {(mapData?.items || []).map(item => (
          <div key={item.id} className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`allergen-map-item-${item.id}`}>
            <span className="font-semibold text-slate-800 min-w-[160px]">{item.item_name}</span>
            <div className="flex flex-wrap gap-1.5 flex-1">
              {tags.map(tag => (
                <Badge
                  key={tag}
                  onClick={() => toggleItemTag(item, tag)}
                  className={`text-xs cursor-pointer select-none ${item.allergens.includes(tag) ? `text-white ${TAG_COLORS[tag] || 'bg-slate-600'}` : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                  data-testid={`map-item-tag-${item.id}-${tag}`}
                >
                  {tag}
                </Badge>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-500 hover:text-red-700" data-testid={`delete-map-item-${item.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}

        <div className="p-3 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 space-y-2" data-testid="add-allergen-item-form">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Item name (e.g. Pizza)"
              className="max-w-xs"
              data-testid="new-allergen-item-name"
            />
            <div className="flex flex-wrap gap-1.5">
              {tags.map(tag => (
                <Badge
                  key={tag}
                  onClick={() => toggleNewTag(tag)}
                  className={`text-xs cursor-pointer select-none ${newTags.includes(tag) ? `text-white ${TAG_COLORS[tag] || 'bg-slate-600'}` : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                  data-testid={`new-item-tag-${tag}`}
                >
                  {tag}
                </Badge>
              ))}
            </div>
            <Button onClick={handleAdd} disabled={saving} className="bg-[#2B4F8C]" data-testid="add-allergen-item-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Add Item
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Override form + list for one camper ──
const CamperOverridePanel = ({ camper, allergenTags, onChanged }) => {
  const [ovType, setOvType] = useState('allergen');
  const [ovValue, setOvValue] = useState('');
  const [note, setNote] = useState('');
  const [setBy, setSetBy] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!ovValue.trim()) { toast.error(ovType === 'allergen' ? 'Select an allergen' : 'Enter an item name'); return; }
    if (!note.trim()) { toast.error('A written permission note is required'); return; }
    if (!setBy.trim()) { toast.error('Enter your name (who is authorizing this)'); return; }
    setSaving(true);
    try {
      await api.addDietaryOverride({
        person_id: camper.person_id,
        type: ovType,
        value: ovValue.trim(),
        note: note.trim(),
        set_by: setBy.trim(),
      });
      toast.success(`Override added for ${camper.name}`);
      setOvValue(''); setNote('');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add override'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (ov) => {
    if (!window.confirm(`Remove override "${ov.value}" for ${camper.name}?`)) return;
    try {
      await api.deleteDietaryOverride(ov.id);
      toast.success('Override removed');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  return (
    <div className="mt-3 space-y-3" data-testid={`override-panel-${camper.person_id}`}>
      {(camper.overrides || []).length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-slate-500 uppercase">Active Overrides</p>
          {camper.overrides.map(ov => (
            <div key={ov.id} className="flex items-start gap-2 p-2 rounded bg-green-50 border border-green-200" data-testid={`override-${ov.id}`}>
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm">
                <span className="font-semibold">{ov.type === 'allergen' ? `Allergen: ${ov.value}` : `Item: ${ov.value}`}</span>
                <p className="text-slate-600 text-xs">"{ov.note}" — {ov.set_by}, {ov.timestamp}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(ov)} className="text-red-500 h-7 px-2" data-testid={`delete-override-${ov.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
        <p className="text-xs font-bold text-slate-500 uppercase">Add Dietary Override (written permission)</p>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={ovType}
            onChange={(e) => { setOvType(e.target.value); setOvValue(''); }}
            className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
            data-testid="override-type-select"
          >
            <option value="allergen">Allow allergen</option>
            <option value="item">Allow specific item</option>
          </select>
          {ovType === 'allergen' ? (
            <select
              value={ovValue}
              onChange={(e) => setOvValue(e.target.value)}
              className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
              data-testid="override-allergen-select"
            >
              <option value="">Select allergen...</option>
              {allergenTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          ) : (
            <Input
              value={ovValue}
              onChange={(e) => setOvValue(e.target.value)}
              placeholder="Item name (e.g. Pizza)"
              className="max-w-[200px] h-9"
              data-testid="override-item-input"
            />
          )}
          <Input
            value={setBy}
            onChange={(e) => setSetBy(e.target.value)}
            placeholder="Your name"
            className="max-w-[160px] h-9"
            data-testid="override-setby-input"
          />
        </div>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder='Permission note, e.g. "Parent confirmed on 6/20 that lactose-free cheese is OK"'
          data-testid="override-note-input"
        />
        <Button onClick={handleAdd} disabled={saving} size="sm" className="bg-[#2B4F8C]" data-testid="add-override-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          Add Override
        </Button>
      </div>
    </div>
  );
};

const CamperCard = ({ camper, allergenTags, onChanged }) => (
  <Card className="border-2 border-slate-200" data-testid={`dietary-camper-card-${camper.person_id}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2">
        <User className="w-5 h-5 text-[#2B4F8C]" />
        <h3 className="font-bold text-slate-900">{camper.name}</h3>
        {camper.group_name && <Badge className="bg-slate-200 text-slate-700 text-xs">{camper.group_name}</Badge>}
      </div>
      <div className="mt-2 space-y-1 text-sm">
        <p className="text-slate-600">
          <span className="font-semibold">Allergy text:</span>{' '}
          {(camper.allergies || []).length > 0 ? camper.allergies.join(', ') : 'None'}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-semibold text-slate-600">Matched allergens:</span>
          {camper.allergens.length > 0
            ? camper.allergens.map(t => <TagBadge key={t} tag={t} />)
            : <span className="text-slate-400">none</span>}
        </div>
        {camper.unrecognized?.length > 0 && (
          <p className="text-amber-700 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Unrecognized allergy text: {camper.unrecognized.join(', ')} — map the allergen or add an override.
          </p>
        )}
      </div>
      <CamperOverridePanel camper={camper} allergenTags={allergenTags} onChanged={onChanged} />
    </CardContent>
  </Card>
);

// ── Main Page ──
export default function AdminDietary() {
  const [mapData, setMapData] = useState(null);
  const [review, setReview] = useState(null);
  const [log, setLog] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshAll = useCallback(async () => {
    try {
      const [mapRes, reviewRes, logRes] = await Promise.all([
        api.getAllergenMap(), api.getAllergyReview(), api.getAllergenLog(),
      ]);
      setMapData(mapRes.data);
      setReview(reviewRes.data);
      setLog(logRes.data.events || []);
    } catch { toast.error('Failed to load dietary data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const runSearch = async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.searchCampersAdmin(q.trim());
      setResults(res.data.results || []);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 400);
    return () => clearTimeout(t);
  }, [query]);

  const refreshSearchAndAll = () => { refreshAll(); runSearch(query); };

  const allergenTags = mapData?.allergen_tags || [];

  if (loading) {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" /></div>;
  }

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="admin-dietary-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-[#2B4F8C]" />
          Dietary &amp; Allergen Blocking
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Orders are blocked only when a camper's matched allergen intersects the item's allergen tags.
          A written dietary override lifts the block for exactly what it names.
        </p>
      </div>

      {/* Needs Review */}
      {review?.count > 0 && (
        <Card className="border-2 border-amber-300 bg-amber-50" data-testid="allergy-review-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Needs Dietary Review ({review.count})
            </CardTitle>
            <p className="text-sm text-amber-700">
              These campers have real allergy text that doesn't match any known allergen keyword.
              They are NOT blocked — review each and map the allergen or add an override.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {review.campers.map(c => (
              <div key={c.person_id} className="flex flex-wrap items-center gap-2 p-2 rounded bg-white border border-amber-200" data-testid={`review-camper-${c.person_id}`}>
                <span className="font-semibold text-slate-800">{c.name}</span>
                {c.group_name && <Badge className="bg-slate-200 text-slate-700 text-xs">{c.group_name}</Badge>}
                <span className="text-sm text-amber-800">"{c.unrecognized.join('", "')}"</span>
                {c.allergens.length > 0 && (
                  <span className="text-xs text-slate-500">(also matched: {c.allergens.join(', ')})</span>
                )}
                {c.overrides_count > 0 && (
                  <Badge className="bg-green-600 text-white text-xs">{c.overrides_count} override(s)</Badge>
                )}
                <Button variant="outline" size="sm" className="ml-auto h-7 text-xs" onClick={() => setQuery(c.name)} data-testid={`review-manage-${c.person_id}`}>
                  Manage
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <AllergenMapCard mapData={mapData} refresh={refreshAll} />

      {/* Camper search + overrides */}
      <Card className="border-2 border-slate-200" data-testid="camper-override-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="w-5 h-5 text-[#2B4F8C]" />
            Camper Dietary Overrides
          </CardTitle>
          <p className="text-sm text-slate-500">
            Search a camper to view their matched allergens and add a written override (admin only).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search camper by name..."
              className="pl-9"
              data-testid="camper-search-input"
            />
            {query && (
              <button onClick={() => { setQuery(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="camper-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searching && <Loader2 className="w-5 h-5 animate-spin text-[#2B4F8C]" />}
          <div className="space-y-3">
            {results.map(c => (
              <CamperCard key={c.person_id} camper={c} allergenTags={allergenTags} onChanged={refreshSearchAndAll} />
            ))}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <p className="text-sm text-slate-400" data-testid="camper-search-empty">No campers found for "{query}"</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Keyword reference */}
      <Card className="border-2 border-slate-200" data-testid="keyword-map-card">
        <CardHeader>
          <CardTitle className="text-lg">Allergen Keyword Reference</CardTitle>
          <p className="text-sm text-slate-500">
            How allergy text on a camper's profile is matched to allergen tags (substring match).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase mb-2">Camper allergy text → allergen</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(mapData?.keyword_map || {}).map(([tag, kws]) => (
                <div key={tag} className="flex items-start gap-2 p-2 rounded bg-slate-50">
                  <TagBadge tag={tag} />
                  <span className="text-sm text-slate-600">{kws.join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase mb-2">Item name → auto-tagged allergen</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(mapData?.item_name_keywords || {}).map(([tag, kws]) => (
                <div key={tag} className="flex items-start gap-2 p-2 rounded bg-amber-50">
                  <TagBadge tag={tag} />
                  <span className="text-sm text-slate-600">{kws.join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity log */}
      <Card className="border-2 border-slate-200" data-testid="allergen-log-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-[#2B4F8C]" />
            Recent Allergen Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-sm text-slate-400">No blocked orders or override activity yet.</p>
          ) : (
            <div className="space-y-1.5">
              {log.map(ev => (
                <div key={ev.id} className="flex flex-wrap items-center gap-2 text-sm p-2 rounded bg-slate-50" data-testid={`log-event-${ev.id}`}>
                  <Badge className={`text-white text-xs ${ev.event === 'order_blocked' ? 'bg-red-600' : ev.event === 'counselor_override_used' ? 'bg-amber-600' : ev.event === 'override_used' ? 'bg-green-600' : 'bg-slate-500'}`}>
                    {ev.event.replace(/_/g, ' ')}
                  </Badge>
                  <span className="font-medium text-slate-800">{ev.camper_name}</span>
                  <span className="text-slate-600">{ev.item}</span>
                  <span className="text-xs text-slate-400 ml-auto">{ev.timestamp}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
